import React from 'react'
import { formatEuroCentsIntoEurWithCommaFixedDecimalsIfNeeded } from 'payment-common/src/vtj/payment-v2.util'
import { useTranslation } from 'react-i18next'

type PriceListTableCell =
  | {
      text: string | React.ReactElement
      tooltip?: React.ReactElement
    }
  | {
      price: number
      perUnit?: string
    }

export type PriceListTableRow = [PriceListTableCell, PriceListTableCell]

type PriceListTableProps = {
  tables: [PriceListTableRow, ...PriceListTableRow[]][]
  footer?: React.ReactElement
}

const PriceListTable: React.FC<PriceListTableProps> = ({ tables, footer }) => {
  let rowIndexFromStart = 0
  function incrementRowIndex<T>(
    mapFn: (item: T, rowIndex: number, mapIndex: number) => React.JSX.Element
  ) {
    return (item: T, mapIndex: number) => {
      rowIndexFromStart++
      return mapFn(item, rowIndexFromStart, mapIndex)
    }
  }
  return (
    <>
      {tables.map(
        incrementRowIndex((rows, rowIndex, mapIndex) => (
          <table key={rowIndex}>
            <thead>
              <tr className={rowIndex % 2 ? 'odd' : 'even'}>
                <th scope="col">
                  <PriceListCell {...rows[0][0]} />
                </th>
                <th scope="col">
                  <PriceListCell {...rows[0][1]} />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(1).map(
                incrementRowIndex(([left, right], rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 ? 'odd' : 'even'}>
                    <th scope="row">
                      <PriceListCell {...left} />
                    </th>
                    <td>
                      <PriceListCell {...right} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {footer && mapIndex + 1 === tables.length && (
              <tfoot>
                <tr>
                  <td
                    headers=""
                    colSpan={2}
                    className={(rowIndex + 1) % 2 ? 'odd' : 'even'}
                  >
                    {footer}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        ))
      )}
    </>
  )
}

export default PriceListTable

const PriceListCell: React.FC<PriceListTableCell> = (cell) => {
  return (
    <>
      {'text' in cell ? <>{cell.text}</> : <Price {...cell} />}
      {'tooltip' in cell && cell.tooltip}
    </>
  )
}

const Price: React.FC<{ price: number; perUnit?: string }> = ({
  price,
  perUnit,
}) => {
  const { t } = useTranslation()
  const nbsp = '\u00a0'
  const cost = [
    formatEuroCentsIntoEurWithCommaFixedDecimalsIfNeeded(price),
    '€',
  ].join(nbsp)
  const unitString = perUnit ? ` /${nbsp}${t(perUnit)}` : ''
  return <>{`${cost}${unitString}`}</>
}
